import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { 
  BarChart3, TrendingUp, TrendingDown, Download, Filter,
  Target, Award, Zap, Calendar, Tag, Clock, RefreshCw,
  ChevronUp, ChevronDown, FileSpreadsheet, X, ArrowRight
} from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Custom Tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-zinc-900 border border-white/10 rounded-lg p-3 shadow-xl">
        <p className="text-white font-medium text-sm">{label}</p>
        {payload.map((p) => (
          <p key={p.dataKey || p.name} className={`text-sm ${p.value >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
            {p.name}: ₹{p.value?.toLocaleString('en-IN')}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Section Card Component
const SectionCard = ({ title, icon: Icon, children, className = "" }) => (
  <div className={`rounded-xl border border-white/5 bg-zinc-900/40 backdrop-blur-md overflow-hidden ${className}`}>
    <div className="flex items-center gap-2 p-4 border-b border-white/5">
      {Icon && <Icon className="w-4 h-4 text-cyan-400" />}
      <h3 className="font-mono text-xs uppercase tracking-widest text-zinc-400">{title}</h3>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

// Stat Badge Component
const StatBadge = ({ label, value, variant = "default" }) => {
  const variants = {
    default: "bg-zinc-800 text-white",
    profit: "bg-emerald-500/20 text-emerald-400",
    loss: "bg-rose-500/20 text-rose-400",
    warning: "bg-amber-500/20 text-amber-400"
  };
  
  return (
    <div className={`px-3 py-1.5 rounded-lg ${variants[variant]}`}>
      <span className="text-[10px] uppercase text-zinc-500 block">{label}</span>
      <span className="font-mono text-sm font-bold">{value}</span>
    </div>
  );
};

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [strategies, setStrategies] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    account_id: "",
    strategy_id: "",
    from_date: "",
    to_date: ""
  });
  
  // Sorting for tables
  const [strategySortKey, setStrategySortKey] = useState("total_pnl");
  const [strategySortDir, setStrategySortDir] = useState("desc");
  const [symbolSortKey, setSymbolSortKey] = useState("total_pnl");
  const [symbolSortDir, setSymbolSortDir] = useState("desc");
  const [tagSortKey, setTagSortKey] = useState("count");
  const [tagSortDir, setTagSortDir] = useState("desc");
  const [monthSortKey, setMonthSortKey] = useState("month");
  const [monthSortDir, setMonthSortDir] = useState("desc");
  
  // Drill-down state
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownData, setDrilldownData] = useState(null);
  const [drilldownType, setDrilldownType] = useState("");
  const [drilldownValue, setDrilldownValue] = useState("");
  const [drilldownLabel, setDrilldownLabel] = useState("");
  
  // Drill-down sorting state
  const [subBreakdownSortKey, setSubBreakdownSortKey] = useState("pnl");
  const [subBreakdownSortDir, setSubBreakdownSortDir] = useState("desc");
  const [tradeSortKey, setTradeSortKey] = useState("pnl");
  const [tradeSortDir, setTradeSortDir] = useState("desc");

  // Fetch drill-down data
  const fetchDrilldown = async (type, value, label) => {
    setDrilldownType(type);
    setDrilldownValue(value);
    setDrilldownLabel(label);
    setDrilldownOpen(true);
    setDrilldownLoading(true);
    // Reset sorting to defaults
    setSubBreakdownSortKey("pnl");
    setSubBreakdownSortDir("desc");
    setTradeSortKey("pnl");
    setTradeSortDir("desc");
    
    try {
      const params = new URLSearchParams();
      params.append('drilldown_type', type);
      params.append('drilldown_value', value);
      if (filters.account_id) params.append('account_id', filters.account_id);
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      
      const response = await api.get(`/analytics/drilldown?${params.toString()}`);
      if (response.data.status) {
        setDrilldownData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching drilldown:", error);
      toast.error(error.userMessage || "Failed to load details");
    } finally {
      setDrilldownLoading(false);
    }
  };

  // Fetch filter options
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [accountsRes, strategiesRes] = await Promise.all([
          api.get("/accounts"),
          api.get("/strategies")
        ]);
        setAccounts(accountsRes.data || []);
        setStrategies(strategiesRes.data || []);
      } catch (error) {
        console.error("Error fetching options:", error);
      }
    };
    fetchOptions();
  }, []);

  // Fetch analytics data
  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.account_id) params.append('account_id', filters.account_id);
      if (filters.strategy_id) params.append('strategy_id', filters.strategy_id);
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      
      const response = await api.get(`/analytics/detailed?${params.toString()}`);
      if (response.data.status) {
        setData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error(error.userMessage || "Failed to load analytics");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Export to Excel
  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.account_id) params.append('account_id', filters.account_id);
      if (filters.strategy_id) params.append('strategy_id', filters.strategy_id);
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      params.append('status', 'CLOSED');
      
      const response = await api.get(`/analytics/export?${params.toString()}`);
      
      if (response.data.status && response.data.data.length > 0) {
        // Convert to CSV and download
        const headers = Object.keys(response.data.data[0]);
        const csvContent = [
          headers.join(','),
          ...response.data.data.map(row => 
            headers.map(h => {
              const val = row[h];
              // Escape values with commas or quotes
              if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
                return `"${val.replace(/"/g, '""')}"`;
              }
              return val ?? '';
            }).join(',')
          )
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `trades_export_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success(`Exported ${response.data.count} trades`);
      } else {
        toast.error("No trades to export");
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export trades");
    }
  };

  // Sort helpers
  const sortData = (data, key, direction) => {
    if (!data) return [];
    return [...data].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];
      
      // Handle null/undefined
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';
      
      // String comparison for text fields
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return direction === "asc" 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      }
      
      // Numeric comparison
      const numA = Number(aVal) || 0;
      const numB = Number(bVal) || 0;
      return direction === "asc" ? numA - numB : numB - numA;
    });
  };

  const toggleSort = (currentKey, setKey, currentDir, setDir, clickedKey) => {
    if (currentKey === clickedKey) {
      setDir(currentDir === "asc" ? "desc" : "asc");
    } else {
      setKey(clickedKey);
      setDir("desc");
    }
  };

  const SortIcon = ({ sortKey, currentKey, direction }) => {
    if (sortKey !== currentKey) return <ChevronUp className="w-3 h-3 opacity-30" />;
    return direction === "asc" ? 
      <ChevronUp className="w-3 h-3 text-cyan-400" /> : 
      <ChevronDown className="w-3 h-3 text-cyan-400" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const summary = data?.summary || {};
  const strategyPerf = sortData(data?.strategy_performance || [], strategySortKey, strategySortDir);
  const symbolPerf = sortData(data?.symbol_performance || [], symbolSortKey, symbolSortDir);
  const tagAnalysis = data?.tag_analysis || [];
  const dayAnalysis = data?.day_analysis || [];
  const monthAnalysis = data?.month_analysis || [];

  return (
    <div className="space-y-6 p-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Trade Analytics</h1>
          <p className="text-sm text-zinc-500">Deep analysis of your trading performance</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            className="border-white/10 text-zinc-400 hover:text-white"
            data-testid="export-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="border-white/10 text-zinc-400 hover:text-white"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 p-4 rounded-xl bg-zinc-900/40 border border-white/5">
        <div className="flex items-center gap-2 text-zinc-500">
          <Filter className="w-4 h-4" />
          <span className="text-xs font-mono uppercase">Filters</span>
        </div>
        
        <Select value={filters.account_id || "all"} onValueChange={(v) => setFilters({...filters, account_id: v === "all" ? "" : v})}>
          <SelectTrigger className="w-[140px] bg-black/20 border-white/10 text-white text-xs">
            <SelectValue placeholder="All Accounts" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10">
            <SelectItem value="all">All Accounts</SelectItem>
            {accounts.map(a => (
              <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={filters.strategy_id || "all"} onValueChange={(v) => setFilters({...filters, strategy_id: v === "all" ? "" : v})}>
          <SelectTrigger className="w-[140px] bg-black/20 border-white/10 text-white text-xs">
            <SelectValue placeholder="All Strategies" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10">
            <SelectItem value="all">All Strategies</SelectItem>
            {strategies.map(s => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <input
          type="date"
          value={filters.from_date}
          onChange={(e) => setFilters({...filters, from_date: e.target.value})}
          className="px-3 py-1.5 rounded-md bg-black/20 border border-white/10 text-white text-xs"
          placeholder="From Date"
        />
        
        <input
          type="date"
          value={filters.to_date}
          onChange={(e) => setFilters({...filters, to_date: e.target.value})}
          className="px-3 py-1.5 rounded-md bg-black/20 border border-white/10 text-white text-xs"
          placeholder="To Date"
        />
        
        {(filters.account_id || filters.strategy_id || filters.from_date || filters.to_date) && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setFilters({ account_id: "", strategy_id: "", from_date: "", to_date: "" })}
            className="text-zinc-500 hover:text-white text-xs"
          >
            Clear
          </Button>
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatBadge label="Total Trades" value={summary.total_trades || 0} />
        <StatBadge 
          label="Net P&L" 
          value={`₹${(summary.total_pnl || 0).toLocaleString('en-IN')}`} 
          variant={summary.total_pnl >= 0 ? "profit" : "loss"}
        />
        <StatBadge label="Wins" value={summary.wins || 0} variant="profit" />
        <StatBadge label="Losses" value={summary.losses || 0} variant="loss" />
        <StatBadge 
          label="Win Rate" 
          value={`${summary.win_rate || 0}%`} 
          variant={summary.win_rate >= 50 ? "profit" : "warning"}
        />
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="strategy" className="w-full">
        <TabsList className="bg-zinc-900/50 border border-white/5">
          <TabsTrigger value="strategy" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
            <Target className="w-4 h-4 mr-2" />
            Strategy
          </TabsTrigger>
          <TabsTrigger value="symbol" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
            <BarChart3 className="w-4 h-4 mr-2" />
            Symbol
          </TabsTrigger>
          <TabsTrigger value="tags" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
            <Tag className="w-4 h-4 mr-2" />
            Tags
          </TabsTrigger>
          <TabsTrigger value="time" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
            <Clock className="w-4 h-4 mr-2" />
            Time
          </TabsTrigger>
        </TabsList>

        {/* Strategy Performance Tab */}
        <TabsContent value="strategy" className="mt-4">
          <SectionCard title="Strategy Performance" icon={Target}>
            {strategyPerf.length === 0 ? (
              <p className="text-zinc-500 text-center py-8">No strategy data available</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead 
                        className="text-zinc-500 cursor-pointer hover:text-white"
                        onClick={() => toggleSort(strategySortKey, setStrategySortKey, strategySortDir, setStrategySortDir, "strategy_name")}
                      >
                        <div className="flex items-center gap-1">
                          Strategy <SortIcon sortKey="strategy_name" currentKey={strategySortKey} direction={strategySortDir} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-zinc-500 cursor-pointer hover:text-white"
                        onClick={() => toggleSort(strategySortKey, setStrategySortKey, strategySortDir, setStrategySortDir, "trades")}
                      >
                        <div className="flex items-center gap-1">
                          Trades <SortIcon sortKey="trades" currentKey={strategySortKey} direction={strategySortDir} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-zinc-500 cursor-pointer hover:text-white"
                        onClick={() => toggleSort(strategySortKey, setStrategySortKey, strategySortDir, setStrategySortDir, "win_rate")}
                      >
                        <div className="flex items-center gap-1">
                          Win Rate <SortIcon sortKey="win_rate" currentKey={strategySortKey} direction={strategySortDir} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-zinc-500 cursor-pointer hover:text-white"
                        onClick={() => toggleSort(strategySortKey, setStrategySortKey, strategySortDir, setStrategySortDir, "profit_factor")}
                      >
                        <div className="flex items-center gap-1">
                          Profit Factor <SortIcon sortKey="profit_factor" currentKey={strategySortKey} direction={strategySortDir} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-zinc-500 cursor-pointer hover:text-white"
                        onClick={() => toggleSort(strategySortKey, setStrategySortKey, strategySortDir, setStrategySortDir, "expectancy")}
                      >
                        <div className="flex items-center gap-1">
                          Expectancy <SortIcon sortKey="expectancy" currentKey={strategySortKey} direction={strategySortDir} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-zinc-500 cursor-pointer hover:text-white text-right"
                        onClick={() => toggleSort(strategySortKey, setStrategySortKey, strategySortDir, setStrategySortDir, "total_pnl")}
                      >
                        <div className="flex items-center gap-1 justify-end">
                          Total P&L <SortIcon sortKey="total_pnl" currentKey={strategySortKey} direction={strategySortDir} />
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {strategyPerf.map((s) => (
                      <TableRow 
                        key={s.strategy_id || s.strategy_name} 
                        className="border-white/5 cursor-pointer hover:bg-white/5 transition-colors"
                        onClick={() => fetchDrilldown("strategy", s.strategy_id, s.strategy_name)}
                      >
                        <TableCell className="font-medium text-white">
                          <div className="flex items-center gap-2">
                            {s.strategy_name}
                            <ArrowRight className="w-3 h-3 text-zinc-600 opacity-0 group-hover:opacity-100" />
                          </div>
                        </TableCell>
                        <TableCell className="text-zinc-400">
                          {s.trades} <span className="text-xs text-zinc-600">({s.wins}W/{s.losses}L)</span>
                        </TableCell>
                        <TableCell className={s.win_rate >= 50 ? "text-emerald-400" : "text-amber-400"}>
                          {s.win_rate}%
                        </TableCell>
                        <TableCell className={s.profit_factor >= 1 ? "text-emerald-400" : "text-rose-400"}>
                          {s.profit_factor}
                        </TableCell>
                        <TableCell className={s.expectancy >= 0 ? "text-emerald-400" : "text-rose-400"}>
                          ₹{s.expectancy.toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell className={`text-right font-mono ${s.total_pnl >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                          ₹{s.total_pnl.toLocaleString('en-IN')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </SectionCard>
        </TabsContent>

        {/* Symbol Performance Tab */}
        <TabsContent value="symbol" className="mt-4">
          <SectionCard title="Symbol Performance" icon={BarChart3}>
            {symbolPerf.length === 0 ? (
              <p className="text-zinc-500 text-center py-8">No symbol data available</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead 
                        className="text-zinc-500 cursor-pointer hover:text-white"
                        onClick={() => toggleSort(symbolSortKey, setSymbolSortKey, symbolSortDir, setSymbolSortDir, "symbol")}
                      >
                        <div className="flex items-center gap-1">
                          Symbol <SortIcon sortKey="symbol" currentKey={symbolSortKey} direction={symbolSortDir} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-zinc-500 cursor-pointer hover:text-white"
                        onClick={() => toggleSort(symbolSortKey, setSymbolSortKey, symbolSortDir, setSymbolSortDir, "strategy_name")}
                      >
                        <div className="flex items-center gap-1">
                          Strategy <SortIcon sortKey="strategy_name" currentKey={symbolSortKey} direction={symbolSortDir} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-zinc-500 cursor-pointer hover:text-white"
                        onClick={() => toggleSort(symbolSortKey, setSymbolSortKey, symbolSortDir, setSymbolSortDir, "trades")}
                      >
                        <div className="flex items-center gap-1">
                          Trades <SortIcon sortKey="trades" currentKey={symbolSortKey} direction={symbolSortDir} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-zinc-500 cursor-pointer hover:text-white"
                        onClick={() => toggleSort(symbolSortKey, setSymbolSortKey, symbolSortDir, setSymbolSortDir, "win_rate")}
                      >
                        <div className="flex items-center gap-1">
                          Win Rate <SortIcon sortKey="win_rate" currentKey={symbolSortKey} direction={symbolSortDir} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-zinc-500 cursor-pointer hover:text-white"
                        onClick={() => toggleSort(symbolSortKey, setSymbolSortKey, symbolSortDir, setSymbolSortDir, "profit_factor")}
                      >
                        <div className="flex items-center gap-1">
                          Profit Factor <SortIcon sortKey="profit_factor" currentKey={symbolSortKey} direction={symbolSortDir} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-zinc-500 cursor-pointer hover:text-white text-right"
                        onClick={() => toggleSort(symbolSortKey, setSymbolSortKey, symbolSortDir, setSymbolSortDir, "total_pnl")}
                      >
                        <div className="flex items-center gap-1 justify-end">
                          Total P&L <SortIcon sortKey="total_pnl" currentKey={symbolSortKey} direction={symbolSortDir} />
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {symbolPerf.map((s) => (
                      <TableRow 
                        key={`${s.symbol}-${s.strategy_id || s.strategy_name}`}
                        className="border-white/5 cursor-pointer hover:bg-white/5 transition-colors"
                        onClick={() => fetchDrilldown("symbol_strategy", `${s.symbol}|${s.strategy_id}`, `${s.symbol} - ${s.strategy_name}`)}
                      >
                        <TableCell className="font-medium text-white">{s.symbol}</TableCell>
                        <TableCell className="text-cyan-400 text-sm">{s.strategy_name}</TableCell>
                        <TableCell className="text-zinc-400">
                          {s.trades} <span className="text-xs text-zinc-600">({s.wins}W/{s.losses}L)</span>
                        </TableCell>
                        <TableCell className={s.win_rate >= 50 ? "text-emerald-400" : "text-amber-400"}>
                          {s.win_rate}%
                        </TableCell>
                        <TableCell className={s.profit_factor >= 1 ? "text-emerald-400" : "text-rose-400"}>
                          {s.profit_factor}
                        </TableCell>
                        <TableCell className={`text-right font-mono ${s.total_pnl >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                          ₹{s.total_pnl.toLocaleString('en-IN')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </SectionCard>
        </TabsContent>

        {/* Tag Analysis Tab */}
        <TabsContent value="tags" className="mt-4">
          <SectionCard title="Trade Tag Analysis" icon={Tag}>
            <p className="text-xs text-zinc-500 mb-4">
              Analysis of entry/exit comment tags to identify patterns in your trading decisions.
            </p>
            {tagAnalysis.length === 0 ? (
              <p className="text-zinc-500 text-center py-8">No tag data found in comments</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead 
                        className="text-zinc-500 cursor-pointer hover:text-white"
                        onClick={() => toggleSort(tagSortKey, setTagSortKey, tagSortDir, setTagSortDir, "tag")}
                      >
                        <div className="flex items-center gap-1">
                          Tag <SortIcon sortKey="tag" currentKey={tagSortKey} direction={tagSortDir} />
                        </div>
                      </TableHead>
                      <TableHead className="text-zinc-500">Description</TableHead>
                      <TableHead 
                        className="text-zinc-500 cursor-pointer hover:text-white"
                        onClick={() => toggleSort(tagSortKey, setTagSortKey, tagSortDir, setTagSortDir, "count")}
                      >
                        <div className="flex items-center gap-1">
                          Count <SortIcon sortKey="count" currentKey={tagSortKey} direction={tagSortDir} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-zinc-500 cursor-pointer hover:text-white"
                        onClick={() => toggleSort(tagSortKey, setTagSortKey, tagSortDir, setTagSortDir, "win_rate")}
                      >
                        <div className="flex items-center gap-1">
                          Win Rate <SortIcon sortKey="win_rate" currentKey={tagSortKey} direction={tagSortDir} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-zinc-500 cursor-pointer hover:text-white"
                        onClick={() => toggleSort(tagSortKey, setTagSortKey, tagSortDir, setTagSortDir, "avg_pnl")}
                      >
                        <div className="flex items-center gap-1">
                          Avg P&L <SortIcon sortKey="avg_pnl" currentKey={tagSortKey} direction={tagSortDir} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-zinc-500 text-right cursor-pointer hover:text-white"
                        onClick={() => toggleSort(tagSortKey, setTagSortKey, tagSortDir, setTagSortDir, "total_pnl")}
                      >
                        <div className="flex items-center gap-1 justify-end">
                          Total P&L <SortIcon sortKey="total_pnl" currentKey={tagSortKey} direction={tagSortDir} />
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortData(tagAnalysis, tagSortKey, tagSortDir).map((t) => (
                      <TableRow 
                        key={t.tag} 
                        className="border-white/5 cursor-pointer hover:bg-white/5 transition-colors"
                        onClick={() => fetchDrilldown("tag", t.tag, `${t.tag} - ${t.description}`)}
                      >
                        <TableCell className="font-mono text-cyan-400 font-medium">{t.tag}</TableCell>
                        <TableCell className="text-zinc-400 text-xs">{t.description}</TableCell>
                        <TableCell className="text-zinc-400">
                          {t.count} <span className="text-xs text-zinc-600">({t.wins}W/{t.losses}L)</span>
                        </TableCell>
                        <TableCell className={t.win_rate >= 50 ? "text-emerald-400" : "text-amber-400"}>
                          {t.win_rate}%
                        </TableCell>
                        <TableCell className={t.avg_pnl >= 0 ? "text-emerald-400" : "text-rose-400"}>
                          ₹{t.avg_pnl.toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell className={`text-right font-mono ${t.total_pnl >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                          ₹{t.total_pnl.toLocaleString('en-IN')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </SectionCard>
        </TabsContent>

        {/* Time Analysis Tab */}
        <TabsContent value="time" className="mt-4 space-y-4">
          {/* Day of Week Chart */}
          <SectionCard title="Performance by Day of Week" icon={Calendar}>
            {dayAnalysis.length === 0 ? (
              <p className="text-zinc-500 text-center py-8">No time data available</p>
            ) : (
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dayAnalysis}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="day" stroke="#52525b" fontSize={11} tickLine={false} tickFormatter={(d) => d.slice(0, 3)} />
                    <YAxis stroke="#52525b" fontSize={10} tickLine={false} tickFormatter={(v) => `₹${v}`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="pnl" name="P&L" radius={[4, 4, 0, 0]}>
                      {dayAnalysis.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.pnl >= 0 ? '#10b981' : '#f43f5e'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </SectionCard>

          {/* Monthly Performance */}
          <SectionCard title="Monthly Performance" icon={Clock}>
            {monthAnalysis.length === 0 ? (
              <p className="text-zinc-500 text-center py-8">No monthly data available</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="text-zinc-500">Month</TableHead>
                      <TableHead className="text-zinc-500">Trades</TableHead>
                      <TableHead className="text-zinc-500">Win Rate</TableHead>
                      <TableHead className="text-zinc-500 text-right">P&L</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {monthAnalysis.map((m) => (
                      <TableRow key={m.month} className="border-white/5">
                        <TableCell className="font-medium text-white">{m.month}</TableCell>
                        <TableCell className="text-zinc-400">{m.trades}</TableCell>
                        <TableCell className={m.win_rate >= 50 ? "text-emerald-400" : "text-amber-400"}>
                          {m.win_rate}%
                        </TableCell>
                        <TableCell className={`text-right font-mono ${m.pnl >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                          ₹{m.pnl.toLocaleString('en-IN')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </SectionCard>
        </TabsContent>
      </Tabs>

      {/* Drill-down Dialog */}
      <Dialog open={drilldownOpen} onOpenChange={setDrilldownOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {drilldownType === "strategy" && <Target className="w-5 h-5 text-cyan-400" />}
              {drilldownType === "symbol" && <BarChart3 className="w-5 h-5 text-cyan-400" />}
              {drilldownType === "tag" && <Tag className="w-5 h-5 text-cyan-400" />}
              {drilldownLabel}
            </DialogTitle>
          </DialogHeader>
          
          {drilldownLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : drilldownData ? (
            <div className="flex-1 overflow-y-auto space-y-4 pr-2">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatBadge label="Trades" value={drilldownData.summary?.total_trades || 0} />
                <StatBadge 
                  label="Net P&L" 
                  value={`₹${(drilldownData.summary?.total_pnl || 0).toLocaleString('en-IN')}`}
                  variant={drilldownData.summary?.total_pnl >= 0 ? "profit" : "loss"}
                />
                <StatBadge 
                  label="Win Rate" 
                  value={`${drilldownData.summary?.win_rate || 0}%`}
                  variant={drilldownData.summary?.win_rate >= 50 ? "profit" : "warning"}
                />
                <StatBadge 
                  label="Profit Factor" 
                  value={drilldownData.summary?.profit_factor || 0}
                  variant={drilldownData.summary?.profit_factor >= 1 ? "profit" : "loss"}
                />
              </div>
              
              {/* Sub-breakdown */}
              {drilldownData.sub_breakdown?.length > 0 && (
                <div className="rounded-lg border border-white/10 overflow-hidden">
                  <div className="px-4 py-2 bg-white/5 border-b border-white/10">
                    <h4 className="text-xs uppercase text-zinc-500 font-mono">
                      Breakdown by {drilldownType === "symbol" ? "Strategy" : drilldownType === "symbol_strategy" ? "Entry Tag" : "Symbol"}
                    </h4>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/5">
                          <TableHead 
                            className="text-zinc-500 text-xs cursor-pointer hover:text-white"
                            onClick={() => toggleSort(subBreakdownSortKey, setSubBreakdownSortKey, subBreakdownSortDir, setSubBreakdownSortDir, "name")}
                          >
                            <div className="flex items-center gap-1">
                              Name <SortIcon sortKey="name" currentKey={subBreakdownSortKey} direction={subBreakdownSortDir} />
                            </div>
                          </TableHead>
                          <TableHead 
                            className="text-zinc-500 text-xs cursor-pointer hover:text-white"
                            onClick={() => toggleSort(subBreakdownSortKey, setSubBreakdownSortKey, subBreakdownSortDir, setSubBreakdownSortDir, "trades")}
                          >
                            <div className="flex items-center gap-1">
                              Trades <SortIcon sortKey="trades" currentKey={subBreakdownSortKey} direction={subBreakdownSortDir} />
                            </div>
                          </TableHead>
                          <TableHead 
                            className="text-zinc-500 text-xs cursor-pointer hover:text-white"
                            onClick={() => toggleSort(subBreakdownSortKey, setSubBreakdownSortKey, subBreakdownSortDir, setSubBreakdownSortDir, "win_rate")}
                          >
                            <div className="flex items-center gap-1">
                              Win Rate <SortIcon sortKey="win_rate" currentKey={subBreakdownSortKey} direction={subBreakdownSortDir} />
                            </div>
                          </TableHead>
                          <TableHead 
                            className="text-zinc-500 text-xs text-right cursor-pointer hover:text-white"
                            onClick={() => toggleSort(subBreakdownSortKey, setSubBreakdownSortKey, subBreakdownSortDir, setSubBreakdownSortDir, "pnl")}
                          >
                            <div className="flex items-center gap-1 justify-end">
                              P&L <SortIcon sortKey="pnl" currentKey={subBreakdownSortKey} direction={subBreakdownSortDir} />
                            </div>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortData(drilldownData.sub_breakdown, subBreakdownSortKey, subBreakdownSortDir).map((item) => (
                          <TableRow key={item.name} className="border-white/5">
                            <TableCell className="text-white text-sm">{item.name}</TableCell>
                            <TableCell className="text-zinc-400 text-sm">{item.trades}</TableCell>
                            <TableCell className={`text-sm ${item.win_rate >= 50 ? "text-emerald-400" : "text-amber-400"}`}>
                              {item.win_rate}%
                            </TableCell>
                            <TableCell className={`text-right font-mono text-sm ${item.pnl >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                              ₹{item.pnl.toLocaleString('en-IN')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
              
              {/* Trade List */}
              <div className="rounded-lg border border-white/10 overflow-hidden">
                <div className="px-4 py-2 bg-white/5 border-b border-white/10 flex items-center justify-between">
                  <h4 className="text-xs uppercase text-zinc-500 font-mono">
                    Individual Trades
                  </h4>
                  <span className="text-xs text-zinc-600">
                    Showing {drilldownData.trades?.length || 0} of {drilldownData.total_trades_count || 0}
                  </span>
                </div>
                <div className="max-h-[300px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/5">
                        <TableHead 
                          className="text-zinc-500 text-xs cursor-pointer hover:text-white"
                          onClick={() => toggleSort(tradeSortKey, setTradeSortKey, tradeSortDir, setTradeSortDir, "symbol")}
                        >
                          <div className="flex items-center gap-1">
                            Symbol <SortIcon sortKey="symbol" currentKey={tradeSortKey} direction={tradeSortDir} />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="text-zinc-500 text-xs cursor-pointer hover:text-white"
                          onClick={() => toggleSort(tradeSortKey, setTradeSortKey, tradeSortDir, setTradeSortDir, "direction")}
                        >
                          <div className="flex items-center gap-1">
                            Direction <SortIcon sortKey="direction" currentKey={tradeSortKey} direction={tradeSortDir} />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="text-zinc-500 text-xs cursor-pointer hover:text-white"
                          onClick={() => toggleSort(tradeSortKey, setTradeSortKey, tradeSortDir, setTradeSortDir, "entry_date")}
                        >
                          <div className="flex items-center gap-1">
                            Entry <SortIcon sortKey="entry_date" currentKey={tradeSortKey} direction={tradeSortDir} />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="text-zinc-500 text-xs cursor-pointer hover:text-white"
                          onClick={() => toggleSort(tradeSortKey, setTradeSortKey, tradeSortDir, setTradeSortDir, "exit_date")}
                        >
                          <div className="flex items-center gap-1">
                            Exit <SortIcon sortKey="exit_date" currentKey={tradeSortKey} direction={tradeSortDir} />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="text-zinc-500 text-xs text-right cursor-pointer hover:text-white"
                          onClick={() => toggleSort(tradeSortKey, setTradeSortKey, tradeSortDir, setTradeSortDir, "pnl")}
                        >
                          <div className="flex items-center gap-1 justify-end">
                            P&L <SortIcon sortKey="pnl" currentKey={tradeSortKey} direction={tradeSortDir} />
                          </div>
                        </TableHead>
                        <TableHead className="text-zinc-500 text-xs">Entry Tag</TableHead>
                        <TableHead className="text-zinc-500 text-xs">Exit Tag</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortData(drilldownData.trades, tradeSortKey, tradeSortDir)?.map((trade, i) => (
                        <TableRow key={i} className="border-white/5">
                          <TableCell className="text-white text-sm">{trade.symbol}</TableCell>
                          <TableCell className={`text-sm ${trade.direction === "LONG" ? "text-emerald-400" : "text-rose-400"}`}>
                            {trade.direction}
                          </TableCell>
                          <TableCell className="text-zinc-400 text-xs">
                            <div>{trade.entry_date}</div>
                            <div className="text-zinc-600">₹{trade.entry_price}</div>
                          </TableCell>
                          <TableCell className="text-zinc-400 text-xs">
                            <div>{trade.exit_date}</div>
                            <div className="text-zinc-600">₹{trade.exit_price}</div>
                          </TableCell>
                          <TableCell className={`text-right font-mono text-sm ${(trade.pnl || 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                            ₹{(trade.pnl || 0).toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell className="text-cyan-400 text-xs truncate max-w-[100px]" title={trade.entry_comments}>
                            {trade.entry_comments || "-"}
                          </TableCell>
                          <TableCell className="text-amber-400 text-xs truncate max-w-[100px]" title={trade.exit_comments}>
                            {trade.exit_comments || "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Analytics;
